import { Ucum, UcumUnit } from '@treetop/shared/model';

/**
 * This function adds the given UCUM string to the given date.
 * For example, if the given UCUM string is '1d' and the given date is '2021-01-01T00:00:00.000Z',
 * the function will return '2021-01-02T00:00:00.000Z'.
 * @param ucum UCUM string to add to the given date
 * @param date Date to add the given UCUM string to, default is the current date
 */
export const ucumToDateFromDate = (ucum: Ucum, date = new Date()): Date => {
  if (!ucum) {
    return null;
  }

  const [time, unit] = ucumToTimeUnit(ucum);
  const newDate = new Date(date);

  switch (unit) {
    case 's':
      newDate.setSeconds(newDate.getSeconds() + time);
      break;
    case 'min':
      newDate.setMinutes(newDate.getMinutes() + time);
      break;
    case 'h':
      newDate.setHours(newDate.getHours() + time);
      break;
    case 'd':
      newDate.setDate(newDate.getDate() + time);
      break;
    case 'wk':
      newDate.setDate(newDate.getDate() + time * 7);
      break;
    case 'mo':
      newDate.setMonth(newDate.getMonth() + time);
      break;
    case 'yr':
      newDate.setFullYear(newDate.getFullYear() + time);
      break;
    default:
      throw new Error('Invalid unit');
  }

  return newDate;
};

/**
 * Converts an UCUM string to [time, unit] tuple.
 */
export const ucumToTimeUnit = (ucum: string): [number, UcumUnit] => {
  if (!ucum || !isUcum(ucum)) {
    return null;
  }

  const time = parseFloat(ucum);
  const unit = ucum.replace(time.toString(), '');
  return [time, unit as UcumUnit];
};

/**
 * Type guard for Ucum (e.g. '1d' is a Ucum, '1' is not)
 * @param value Value to check
 * @returns True if the value is a Ucum, false otherwise
 */
export const isUcum = (value: string): value is Ucum => {
  if (!value) {
    return false;
  }

  const match = value.match(/^(\d+)([a-z]+)$/);
  return match !== null && isUcumUnit(match[2]);
};

/**
 * Type guard for UcumUnit (e.g. 'd' is a UcumUnit, 'test' is not)
 * @param value Value to check
 * @returns True if the value is a UcumUnit, false otherwise
 */
export const isUcumUnit = (value: string): value is UcumUnit => {
  if (!value) {
    return false;
  }

  return /^(wk|mo|yr|d|h|min|s)$/.test(value);
};

/**
 * Converts a UCUM unit to a key that can be used to translate the unit.
 */
export const ucumUnitToTranslateKey = (unit: UcumUnit): string => {
  let unitKey = '';

  switch (unit) {
    case 'yr':
      unitKey = 'InYears';
      break;
    case 'mo':
      unitKey = 'InMonths';
      break;
    case 'wk':
      unitKey = 'InWeeks';
      break;
    case 'd':
      unitKey = 'InDays';
      break;
    case 'h':
      unitKey = 'InHours';
      break;
    case 'min':
      unitKey = 'InMinutes';
      break;
    case 's':
      unitKey = 'InSeconds';
      break;
  }

  return `Ucum.${unitKey}`;
};
